import { BaseModernLayouter } from './BaseModernLayouter';
import { LayoutData, SpacingMode } from '../LayoutData';
import { UpVectorMode, AlignmentMode } from '../../field/FieldTypes';
import { POI } from '../../poi/POI';

// Import our modern layouters (JS files)
declare const ModernLayouters: any;

export class ModernLineAdaptiveLayouter extends BaseModernLayouter {
    constructor() {
        super(
            'modern-line-adaptive', 
            'Modern Line Adaptive Layouter (Height Normalization)',
            {
                modernLineFixedSpacing: 1.0,
                modernFlip180: false
            },
            '1d',
            [SpacingMode.Fixed]
        );
    }

    // ========================================
    // IMPLEMENTATION OF ABSTRACT METHODS
    // ========================================

    protected getModernLayouterFunction(): (config: any) => any[] {
        return ModernLayouters.lineAdaptive;
    }

    protected createConfig(pois: POI[], layoutData: LayoutData): any {
        return {
            objectCount: pois.length,
            spacingMode: this.getSpacingModeString(layoutData),
            spacing: this.getParameter(layoutData, 'modernLineFixedSpacing', 1.0),
            pathType: 'line',
            orientation: layoutData.alignmentMode?.value === AlignmentMode.Aligned ? 'aligned' : 'perpendicular',
            upMode: layoutData.upVectorMode?.value === UpVectorMode.PathUp ? 'path' : 'world',
            flip180: this.getFlipParameter(layoutData),
            layoutDirection: null, // TODO: Add layoutDirection property to LayoutData if needed
            objects: pois.map(poi => {
                // 🎯 Extract REAL object dimensions from mesh geometry bounding box
                const dimensions = this.extractMeshDimensions(poi);
                return {
                    userData: {
                        naturalWidth: dimensions.width,
                        naturalHeight: dimensions.height,
                        width: dimensions.width,
                        height: dimensions.height,
                        aspectRatio: dimensions.aspectRatio
                    }
                };
            }),
            time: 0
        };
    }

    protected getLayouterName(): string {
        return 'ModernLineAdaptiveLayouter';
    }

    validateParameters(layoutData: LayoutData): boolean {
        const spacing = this.getParameter(layoutData, 'modernLineFixedSpacing', 1.0);
        return spacing >= 0;
    }
}

export class ModernCircleAdaptiveLayouter extends BaseModernLayouter {
    constructor() {
        super(
            'modern-circle-adaptive', 
            'Modern Circle Adaptive Layouter (Height Normalization)',
            {
                modernCircleRadius: 5.0, // 🔄 Unified radius for all modern layouters
                modernCircleFixedSpacing: 1.0,
                modernFlip180: false
            },
            '1d',
            [SpacingMode.Fixed, SpacingMode.Distribute]
        );
    }

    // ========================================
    // IMPLEMENTATION OF ABSTRACT METHODS
    // ========================================

    protected getModernLayouterFunction(): (config: any) => any[] {
        return ModernLayouters.circleAdaptive;
    }

    protected createConfig(pois: POI[], layoutData: LayoutData): any {
        return {
            objectCount: pois.length,
            spacingMode: this.getSpacingModeString(layoutData),
            spacing: this.getParameter(layoutData, 'modernCircleFixedSpacing', 1.0),
            pathType: 'circle',
            orientation: layoutData.alignmentMode?.value === AlignmentMode.Aligned ? 'aligned' : 'perpendicular',
            upMode: layoutData.upVectorMode?.value === UpVectorMode.PathUp ? 'path' : 'world',
            flip180: this.getFlipParameter(layoutData),
            radius: this.getParameter(layoutData, 'modernCircleRadius', 5.0), // 🔄 Unified radius for all modern layouters
            layoutDirection: null, // TODO: Add layoutDirection property to LayoutData if needed
            objects: pois.map(poi => {
                // 🎯 Extract REAL object dimensions from mesh geometry bounding box
                const dimensions = this.extractMeshDimensions(poi);
                return {
                    userData: {
                        naturalWidth: dimensions.width,
                        naturalHeight: dimensions.height,
                        width: dimensions.width,
                        height: dimensions.height,
                        aspectRatio: dimensions.aspectRatio
                    }
                };
            }),
            time: 0
        };
    }

    protected getLayouterName(): string {
        return 'ModernCircleAdaptiveLayouter';
    }

    validateParameters(layoutData: LayoutData): boolean {
        const radius = this.getParameter(layoutData, 'modernCircleRadius', 5.0);
        const spacing = this.getParameter(layoutData, 'modernCircleFixedSpacing', 1.0);
        return radius > 0 && spacing >= 0;
    }
}

export class ModernSpiralAdaptiveLayouter extends BaseModernLayouter {
    constructor() {
        super(
            'modern-spiral-adaptive', 
            'Modern Spiral Adaptive Layouter (Height Normalization)',
            {
                modernSpiralFixedSpacing: 1.0,
                modernSpiralTurns: 2.0,
                modernSpiralMaxRadius: 3.0,
                modernSpiralGrowth: 2.0,
                modernSpiralStartDistance: 0.0,
                modernFlip180: false
            },
            '1d',
            [SpacingMode.Fixed]
        );
    }

    // ========================================
    // IMPLEMENTATION OF ABSTRACT METHODS
    // ========================================

    protected getModernLayouterFunction(): (config: any) => any[] {
        return ModernLayouters.spiralAdaptive;
    }

    protected createConfig(pois: POI[], layoutData: LayoutData): any {
        return {
            objectCount: pois.length,
            spacingMode: this.getSpacingModeString(layoutData),
            spacing: this.getParameter(layoutData, 'modernSpiralFixedSpacing', 1.0),
            pathType: 'spiral',
            orientation: layoutData.alignmentMode?.value === AlignmentMode.Aligned ? 'aligned' : 'perpendicular',
            upMode: layoutData.upVectorMode?.value === UpVectorMode.PathUp ? 'path' : 'world',
            flip180: this.getFlipParameter(layoutData),
            layoutDirection: null, // TODO: Add layoutDirection property to LayoutData if needed
            spiralConfig: {
                spiralTurns: this.getParameter(layoutData, 'modernSpiralTurns', 2.0),
                maxRadius: this.getParameter(layoutData, 'modernSpiralMaxRadius', 3.0),
                growthRate: this.getParameter(layoutData, 'modernSpiralGrowth', 2.0),
                startDistance: this.getParameter(layoutData, 'modernSpiralStartDistance', 0.0)
            },
            objects: pois.map(poi => {
                // 🎯 Extract REAL object dimensions from mesh geometry bounding box
                const dimensions = this.extractMeshDimensions(poi);
                return {
                    userData: {
                        naturalWidth: dimensions.width,
                        naturalHeight: dimensions.height,
                        width: dimensions.width,
                        height: dimensions.height,
                        aspectRatio: dimensions.aspectRatio
                    }
                };
            }),
            time: 0
        };
    }

    protected getLayouterName(): string {
        return 'ModernSpiralAdaptiveLayouter';
    }

    validateParameters(layoutData: LayoutData): boolean {
        const spacing = this.getParameter(layoutData, 'modernSpiralFixedSpacing', 1.0);
        const turns = this.getParameter(layoutData, 'modernSpiralTurns', 2.0);
        const maxRadius = this.getParameter(layoutData, 'modernSpiralMaxRadius', 3.0);
        const growthRate = this.getParameter(layoutData, 'modernSpiralGrowth', 2.0);
        
        return spacing >= 0 && turns > 0 && maxRadius > 0 && growthRate > 0;
    }
}